import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { uploadProductImage } from '../api/productImage';
import ProductImage from '../components/ProductImage';
import axiosInstance from '../api/commonAPI';
import { useParams, useNavigate } from 'react-router-dom';

interface Category {
    id: number;
    name: string;
}

interface ProductCreateRequest {
    name: string;
    price: number;
    description: string;
    categoryId: number;
    onDisplay: boolean;
    photo?: {
        originalFileName: string;
        storedFileName: string;
    }
}

const ProductCreatePage: React.FC = () => {
    const { storeId } = useParams<{ storeId: string }>();
    const navigate = useNavigate();

    const [productName, setProductName] = useState<string>('');
    const [price, setPrice] = useState<number>(0);
    const [description, setDescription] = useState<string>('');
    const [categoryId, setCategoryId] = useState<number | ''>('');
    const [productImage, setProductImage] = useState<File | null>(null);
    const [categories, setCategories] = useState<Category[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [onDisplay, setOnDisplay] = useState<boolean>(true);
    const [imagePreview, setImagePreview] = useState<string | null>(null);

    useEffect(() => {
        if (!productImage) { setImagePreview(null); return; }
        const url = URL.createObjectURL(productImage);
        setImagePreview(url);
        return () => URL.revokeObjectURL(url);
    }, [productImage]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await axiosInstance.get<Category[]>(`/api/stores/${storeId}/categories`);
                setCategories(response.data);
                if (response.data.length > 0) {
                    setCategoryId(response.data[0].id);
                }
            } catch (error) {
                console.error('카테고리 목록 조회 실패:', error);
                alert('카테고리 정보를 불러오지 못했습니다.');
            }
        };

        if (storeId) {
            fetchCategories();
        }
    }, [storeId]);

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            if (e.target.files[0] && !e.target.files[0].type.startsWith('image/')) {
                alert('이미지 파일을 선택해 주세요.');
                e.target.value = '';
                return;
            }
            setProductImage(e.target.files[0] || null);
        }
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (isLoading) return;
        if (!productName.trim() || !Number.isInteger(price) || price <= 0 || price > 2147483647 || !categoryId) {
            alert('상품명, 가격, 카테고리를 모두 입력해 주세요.');
            return;
        }
        setIsLoading(true);

        try {
            const photo = productImage ? await uploadProductImage(productImage) : undefined;

            const createRequest: ProductCreateRequest = {
                name: productName.trim(),
                price,
                description,
                categoryId: Number(categoryId),
                onDisplay,
                photo,
            };

            await axiosInstance.post(`/api/stores/${storeId}/admin/products`, createRequest);

            alert('상품이 성공적으로 등록되었습니다.');
            navigate(`/admin/stores/${storeId}/products`);
        } catch (error) {
            console.error('상품 등록 실패:', error);
            alert('상품 등록 중 오류가 발생했습니다. 자세한 내용은 콘솔을 확인해 주세요.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
            <h2>새 상품 등록 (매장 ID: {storeId})</h2>
            <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '15px' }}>
                    <label htmlFor="product-category">카테고리</label>
                    <select
                        id="product-category"
                        value={categoryId}
                        onChange={(e) => setCategoryId(Number(e.target.value))}
                        style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                    >
                        <option value="">카테고리를 선택해 주세요</option>
                        {categories.map(category => (
                            <option key={category.id} value={category.id}>{category.name}</option>
                        ))}
                    </select>
                </div>
                <div style={{ marginBottom: '15px' }}>
                    <label htmlFor="product-name">상품명</label>
                    <input
                        id="product-name"
                        type="text"
                        value={productName}
                        onChange={(e) => setProductName(e.target.value)}
                        style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                    />
                </div>
                <div style={{ marginBottom: '15px' }}>
                    <label htmlFor="product-price">가격</label>
                    <input
                        id="product-price"
                        type="number"
                        value={price}
                        onChange={(e) => setPrice(parseInt(e.target.value, 10))}
                        style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                    />
                </div>
                <div style={{ marginBottom: '15px' }}>
                    <label>상품 설명</label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={4}
                        style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                    />
                </div>
                <div style={{ marginBottom: '20px' }}>
                    <label htmlFor="product-image">상품 이미지 (선택)</label>
                    <ProductImage loading="eager" src={imagePreview || undefined} alt="상품 이미지 미리보기" style={{ display: 'block', width: 200, height: 140, objectFit: 'cover', marginTop: 8 }} />
                    <p>이미지를 선택하지 않으면 기본 이미지가 표시됩니다.</p>
                    <input
                        id="product-image"
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                    />
                </div>

                <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
                    <label htmlFor="onDisplayToggle" style={{ marginRight: '10px' }}>키오스크에 진열</label>
                    <input
                        type="checkbox"
                        id="onDisplayToggle"
                        checked={onDisplay}
                        onChange={(e) => setOnDisplay(e.target.checked)}
                        style={{ width: '20px', height: '20px' }}
                    />
                    <span style={{ marginLeft: '8px' }}>
                        {onDisplay ? '고객 화면에 표시됩니다.' : '고객 화면에 표시되지 않습니다.'}
                    </span>
                </div>

                <button
                    type="submit"
                    disabled={isLoading}
                    style={{ width: '100%', padding: '10px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                    {isLoading ? '등록 중...' : '상품 등록'}
                </button>
            </form>
        </div>
    );
};

export default ProductCreatePage;
